export type ExecutorFunction = () => boolean;
interface ExecutorRetryArgs {
  strategyName: string;
  errorName: string | null;
  error: unknown;
}
export interface ExecutorStrategy {
  readonly name: string;
  execute: (op: ExecutorFunction) => boolean;
  canRetry: (args: ExecutorRetryArgs) => boolean;
  success?: (strategyName: string) => void;
  failed?: (strategyName: string) => void;
  error?: (ex: unknown, strategyName: string) => void;
}
export interface ExecutorOptions {
  strategies?: ExecutorStrategy[];
  failure?: () => void;
}
export class Executor {
  private _strategies: ExecutorStrategy[] = [];

  get strategies(): ReadonlyArray<ExecutorStrategy> {
    return this._strategies;
  }

  constructor(private options?: ExecutorOptions) {
    this.addStrategies(options?.strategies ?? []);
  }

  addStrategies(strategies: ExecutorStrategy[]): void {
    this._strategies.push(...strategies);
  }

  execute(operation: ExecutorFunction): void {
    let retryArgs: ExecutorRetryArgs | null = null;
    for (const strategy of this._strategies) {
      try {
        if (strategy.execute(operation)) {
          strategy.success?.(strategy.name);
          return;
        } else {
          strategy.failed?.(strategy.name);
          retryArgs = this._createRetryArgs(strategy.name);
        }
      } catch (ex) {
        strategy.error?.(ex, strategy.name);
        retryArgs = this._createRetryArgs(strategy.name, ex);
      }
      if (retryArgs != null && !strategy.canRetry(retryArgs)) {
        break;
      }
    }
    if (retryArgs != null) {
      this.options?.failure?.();
    }
  }

  private _createRetryArgs(strategyName: string, error: unknown = null): ExecutorRetryArgs {
    return { strategyName, error, errorName: error instanceof Error ? error.name : null };
  }
}
